import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  Zap, LogOut, Menu, Loader2, Plus, Trash2, Copy, Check,
  LayoutDashboard, Shield, Users, Link2, Eye, EyeOff,
  BarChart3, Clock, AlertTriangle, CheckCircle2
} from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminBeta = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, login: authLogin } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tab, setTab] = useState('users');
  const [betaData, setBetaData] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', notes: '' });
  const [copiedLink, setCopiedLink] = useState('');
  const [impersonating, setImpersonating] = useState(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [bRes, uRes, aRes] = await Promise.all([
        axios.get(`${API}/admin/beta-users`),
        axios.get(`${API}/admin/usage-overview`),
        axios.get(`${API}/admin/audit-log?limit=20`),
      ]);
      setBetaData(bRes.data);
      setUsageData(uRes.data);
      setAuditLog(aRes.data);
    } catch (err) {
      if (err.response?.status === 403) toast.error('Admin access required');
    } finally { setLoading(false); }
  };

  const handleAdd = async () => {
    if (!addForm.email) return toast.error('Email required');
    try {
      await axios.post(`${API}/admin/beta-users`, addForm);
      toast.success('Beta user added');
      setShowAdd(false);
      setAddForm({ email: '', notes: '' });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Remove this beta user?')) return;
    try {
      await axios.delete(`${API}/admin/beta-users/${id}`);
      toast.success('Removed');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const handleToggleStatus = async (id, current) => {
    try {
      await axios.patch(`${API}/admin/beta-users/${id}`, { status: current === 'active' ? 'paused' : 'active' });
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const handleMagicLink = async (id) => {
    try {
      const res = await axios.post(`${API}/admin/beta-users/${id}/create-login-link`);
      const fullUrl = `${window.location.origin}${res.data.url}`;
      navigator.clipboard.writeText(fullUrl);
      setCopiedLink(id);
      toast.success('Login link copied!');
      setTimeout(() => setCopiedLink(''), 3000);
    } catch { toast.error('Failed to generate link'); }
  };

  const handleImpersonate = async (userId) => {
    try {
      const res = await axios.post(`${API}/admin/impersonate/start?target_user_id=${userId}`);
      setImpersonating(res.data);
      // Store admin backup token
      localStorage.setItem('admin_backup_token', res.data.admin_token_backup);
      localStorage.setItem('impersonation_session', res.data.session_id);
      // Switch to impersonated user
      localStorage.setItem('token', res.data.token);
      toast.success(`Impersonating ${res.data.target_user.email}`);
      window.location.reload();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Shield, label: 'Admin: Beta', path: '/admin/beta-users' },
    { icon: BarChart3, label: 'Analytics', path: '/analytics' },
    { icon: Users, label: 'Operations', path: '/operations' },
  ];

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center"><AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" /><p className="font-bold">Admin access required</p></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <div className="noise-overlay" />
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0a0a0a] border-r border-white/5 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/5"><Link to="/" className="flex items-center gap-2"><HSLogo size={32} /><span className="font-black text-lg tracking-tight">HumanScaled</span></Link></div>
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map(item => { const Icon = item.icon; const isActive = location.pathname === item.path; return (<Link key={item.path} to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${isActive ? 'bg-emerald-500/10 text-emerald-400 border-l-2 border-emerald-500' : 'text-muted-foreground hover:text-white hover:bg-white/5'}`}><Icon className="w-5 h-5" />{item.label}</Link>); })}
          </nav>
          <div className="p-4 border-t border-white/5">
            <Button variant="ghost" onClick={() => { logout(); navigate('/'); }} className="w-full justify-start text-muted-foreground hover:text-white"><LogOut className="w-4 h-4 mr-2" /> Sign Out</Button>
          </div>
        </div>
      </aside>
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-muted-foreground hover:text-white"><Menu className="w-6 h-6" /></button>
              <h1 className="text-lg font-bold" data-testid="admin-title">Admin: Private Beta</h1>
            </div>
            <div className="flex items-center gap-3">
              {betaData && <span className="text-xs font-mono px-2 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{betaData.total}/{betaData.max} SLOTS</span>}
              <div className="flex bg-[#0a0a0a] border border-white/10 rounded-sm overflow-hidden">
                {[{ key: 'users', label: 'USERS' }, { key: 'usage', label: 'USAGE' }, { key: 'audit', label: 'AUDIT' }].map(t => (
                  <button key={t.key} onClick={() => setTab(t.key)} className={`px-3 py-1.5 text-xs font-mono ${tab === t.key ? 'bg-emerald-500/20 text-emerald-400' : 'text-muted-foreground hover:text-white'}`}>{t.label}</button>
                ))}
              </div>
              <Button onClick={() => setShowAdd(true)} className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-9 text-sm" data-testid="add-beta-btn"><Plus className="w-4 h-4 mr-1" /> Add User</Button>
            </div>
          </div>
        </header>

        <div className="p-6 lg:p-8">
          {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div> : (
            <>
              {tab === 'users' && betaData && (
                <div className="space-y-3" data-testid="beta-users-list">
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-[#0a0a0a] border border-white/5 p-4"><p className="text-[10px] text-muted-foreground uppercase">Total</p><p className="font-mono text-2xl font-bold">{betaData.total}</p></div>
                    <div className="bg-[#0a0a0a] border border-white/5 p-4"><p className="text-[10px] text-muted-foreground uppercase">Max</p><p className="font-mono text-2xl font-bold">{betaData.max}</p></div>
                    <div className="bg-[#0a0a0a] border border-emerald-500/20 p-4"><p className="text-[10px] text-emerald-400 uppercase">Remaining</p><p className="font-mono text-2xl font-bold text-emerald-400">{betaData.remaining}</p></div>
                  </div>
                  {betaData.users.map(bu => (
                    <div key={bu.id || bu.email} className="bg-[#0a0a0a] border border-white/5 p-4 flex items-center justify-between" data-testid={`beta-user-${bu.email}`}>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm">{bu.email}</p>
                          <span className={`text-[10px] font-mono px-1.5 py-0.5 ${bu.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>{bu.status}</span>
                        </div>
                        {bu.notes && <p className="text-xs text-muted-foreground mt-0.5">{bu.notes}</p>}
                        {bu.last_login_at && <p className="text-[10px] text-muted-foreground mt-0.5">Last login: {new Date(bu.last_login_at).toLocaleString()}</p>}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button onClick={() => handleMagicLink(bu.id)} variant="outline" size="sm" className="border-white/10 rounded-sm h-7 text-[10px] px-2" data-testid={`magic-${bu.email}`}>
                          {copiedLink === bu.id ? <Check className="w-3 h-3 text-emerald-400" /> : <Link2 className="w-3 h-3 mr-0.5" />} {copiedLink === bu.id ? 'Copied' : 'Login Link'}
                        </Button>
                        <Button onClick={() => { /* Need user_id - look up by email */ (async () => { const u = await axios.get(`${API}/auth/me`); /* simplified - impersonate by looking up */ })(); }} variant="outline" size="sm" className="border-violet-500/20 text-violet-400 rounded-sm h-7 text-[10px] px-2">
                          <Eye className="w-3 h-3 mr-0.5" /> View
                        </Button>
                        <Button onClick={() => handleToggleStatus(bu.id, bu.status)} variant="outline" size="sm" className="border-white/10 rounded-sm h-7 text-[10px] px-2">
                          {bu.status === 'active' ? 'Pause' : 'Activate'}
                        </Button>
                        <Button onClick={() => handleRemove(bu.id)} variant="outline" size="sm" className="border-red-500/20 text-red-400 rounded-sm h-7 text-[10px] px-2"><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {tab === 'usage' && usageData && (
                <div className="space-y-6" data-testid="usage-tab">
                  <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Usage — Last 24 Hours</h2>
                  {usageData.usage_24h.length > 0 ? (
                    <div className="bg-[#0a0a0a] border border-white/5 overflow-hidden">
                      <table className="w-full">
                        <thead><tr className="border-b border-white/5">
                          <th className="text-left p-3 text-xs text-muted-foreground">User</th>
                          <th className="text-left p-3 text-xs text-muted-foreground">Tokens</th>
                          <th className="text-left p-3 text-xs text-muted-foreground">Cost</th>
                          <th className="text-left p-3 text-xs text-muted-foreground">Calls</th>
                        </tr></thead>
                        <tbody className="divide-y divide-white/5">
                          {usageData.usage_24h.map((u, i) => (
                            <tr key={i} className="hover:bg-white/5">
                              <td className="p-3 text-sm">{u.email}</td>
                              <td className="p-3 font-mono text-sm text-emerald-400">{u.tokens}</td>
                              <td className="p-3 font-mono text-sm">${u.cost}</td>
                              <td className="p-3 font-mono text-sm">{u.calls}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : <p className="text-sm text-muted-foreground">No usage in last 24h</p>}

                  <h2 className="text-sm font-bold uppercase tracking-wider text-muted-foreground">Usage — Last 7 Days</h2>
                  {usageData.usage_7d.length > 0 ? (
                    <div className="bg-[#0a0a0a] border border-white/5 overflow-hidden">
                      <table className="w-full">
                        <thead><tr className="border-b border-white/5">
                          <th className="text-left p-3 text-xs text-muted-foreground">User</th>
                          <th className="text-left p-3 text-xs text-muted-foreground">Tokens</th>
                          <th className="text-left p-3 text-xs text-muted-foreground">Cost</th>
                          <th className="text-left p-3 text-xs text-muted-foreground">Calls</th>
                        </tr></thead>
                        <tbody className="divide-y divide-white/5">
                          {usageData.usage_7d.map((u, i) => (
                            <tr key={i} className="hover:bg-white/5">
                              <td className="p-3 text-sm">{u.email}</td>
                              <td className="p-3 font-mono text-sm text-emerald-400">{u.tokens}</td>
                              <td className="p-3 font-mono text-sm">${u.cost}</td>
                              <td className="p-3 font-mono text-sm">{u.calls}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : <p className="text-sm text-muted-foreground">No usage in last 7d</p>}
                </div>
              )}

              {tab === 'audit' && (
                <div className="space-y-2" data-testid="audit-tab">
                  {auditLog.map((log, i) => (
                    <div key={i} className="bg-[#0a0a0a] border border-white/5 p-3 flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span className="font-mono px-1.5 py-0.5 bg-white/5 text-muted-foreground">{log.action}</span>
                        {log.target_user_id && <span className="text-muted-foreground">target: {log.target_user_id?.slice(0, 8)}</span>}
                      </div>
                      <span className="text-muted-foreground">{new Date(log.created_at).toLocaleString()}</span>
                    </div>
                  ))}
                  {auditLog.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No audit events</p>}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-[#0a0a0a] border-white/10 max-w-sm">
          <DialogHeader><DialogTitle>Add Beta User</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-3">
            <Input value={addForm.email} onChange={e => setAddForm({ ...addForm, email: e.target.value })} placeholder="friend@email.com" className="bg-black/50 border-white/10 rounded-sm" data-testid="add-email" />
            <Textarea value={addForm.notes} onChange={e => setAddForm({ ...addForm, notes: e.target.value })} placeholder="Notes (optional)" className="bg-black/50 border-white/10 rounded-sm min-h-[50px]" />
            <Button onClick={handleAdd} className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm" data-testid="add-submit">Add to Beta</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBeta;
